import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { api } from '../../lib/api'
import { bus } from '../../App'
import { useSelectionStore } from '../../state/selectionStore'
import type { AgentRecord } from '../../../shared/types/agents'
import type { StoredPrompt } from '../../../shared/types/context'
import { TagEditor } from '../TagEditor'
import { ToolSelector } from '../ToolSelector'
import { ClickToCopy } from '../ClickToCopy'
import { models } from '../../../shared/lib/models'

type AgentFormState = {
  name: string
  system_prompt_hash: string
  provider: string
  model: string
  tools: string[]
}

type TagEditorState = {
  agentId: string
  entityId: number
  anchorEl: HTMLElement
} | null

const INITIAL_FORM: AgentFormState = {
  name: '',
  system_prompt_hash: '',
  provider: '',
  model: '',
  tools: [],
}

const PROMPT_HASH_PATTERN = /^[a-f0-9]{32,64}$/i

function normalizeAgent(row: any): AgentRecord {
  return {
    id: String(row?.id ?? ''),
    name: row?.name ?? null,
    system_prompt_hash: row?.system_prompt_hash ?? null,
    provider: row?.provider ?? null,
    model: row?.model ?? null,
    tools: row?.tools ?? null,
    last_message_id: row?.last_message_id ?? null,
    state: row?.state ?? null,
    created_at: Number(row?.created_at ?? Date.now()),
    updated_at: Number(row?.updated_at ?? Date.now()),
    last_run_at: row?.last_run_at ?? null,
    tag_entity_id: row?.tag_entity_id ?? null,
  }
}

function slugifyName(value: string): string {
  const trimmed = value.trim().toLowerCase()
  if (!trimmed) {
    return ''
  }
  return trimmed
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

function deriveTagEntityId(agentId: string): number {
  let h1 = 0xdeadbeef ^ agentId.length
  let h2 = 0x41c6ce57 ^ agentId.length
  for (let i = 0; i < agentId.length; i += 1) {
    const ch = agentId.charCodeAt(i)
    h1 = Math.imul(h1 ^ ch, 2654435761)
    h2 = Math.imul(h2 ^ ch, 1597334677)
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909)
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909)
  const combined = 4294967296 * (2097151 & h2) + (h1 >>> 0)
  return combined
}

function formatPromptSnippet(content: string, maxLength = 500): string {
  const normalized = content.replace(/\s+/g, ' ').trim()
  if (normalized.length <= maxLength) {
    return normalized
  }
  return `${normalized.slice(0, maxLength - 3)}...`
}

function buildPromptSortWeight(prompt: StoredPrompt): number {
  const base = prompt.last_used_at ?? prompt.created_at
  return prompt.is_composed ? base + 10_000_000_000 : base
}

export function AgentManagerPanel() {
  const [agents, setAgents] = useState<AgentRecord[]>([])
  const agentsRef = useRef<AgentRecord[]>([])
  const [agentTags, setAgentTags] = useState<Record<string, string[]>>({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [cloning, setCloning] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [prompts, setPrompts] = useState<StoredPrompt[]>([])
  const [promptSearch, setPromptSearch] = useState('')
  const [promptLoading, setPromptLoading] = useState(true)
  const [tagEditorState, setTagEditorState] = useState<TagEditorState>(null)
  const [formState, setFormState] = useState<AgentFormState>(INITIAL_FORM)
  const [activationNotice, setActivationNotice] = useState<string | null>(null)
  const [activeAgentId, setActiveAgentId] = useState<string | null>(null)
  const [promptPreviewExpanded, setPromptPreviewExpanded] = useState(false)

  const mountedRef = useRef(true)
  const selection = useSelectionStore((state) => state.agent_id)
  const select = useSelectionStore((state) => state.set)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  useEffect(() => {
    const loadActiveAgentId = async () => {
      try {
        const activeId = await api().services.meta.get('active_agent_id')
        if (activeId && mountedRef.current) {
          setActiveAgentId(activeId)
        }
      } catch (err) {
        console.error('[AgentManagerPanel] Failed to load active agent ID:', err)
      }
    }
    void loadActiveAgentId()
  }, [])

  const promptMap = useMemo(() => {
    const next: Record<string, StoredPrompt> = {}
    for (const prompt of prompts) {
      next[prompt.hash] = prompt
    }
    return next
  }, [prompts])

  const modelKeyMap = useMemo(() => {
    const next: Record<string, string> = {}
    for (const model of models) {
      next[model.model] = model.key
    }
    return next
  }, [])

  useEffect(() => {
    agentsRef.current = agents
  }, [agents])

  const selectedAgent = useMemo(() => {
    if (!selection) {
      return null
    }
    return agents.find((agent) => agent.id === selection) ?? null
  }, [agents, selection])

  const ensurePromptLoaded = useCallback(
    async (hash: string | null | undefined) => {
      const normalizedHash = hash ?? ''
      if (!normalizedHash || promptMap[normalizedHash]) {
        return
      }
      try {
        const prompt = await api().services.context.getPrompt(normalizedHash)
        if (prompt && mountedRef.current) {
          setPrompts((prev) => {
            const existing = prev.find((candidate) => candidate.hash === prompt.hash)
            if (existing) {
              return prev
            }
            const next = [...prev, prompt]
            next.sort((a, b) => buildPromptSortWeight(b) - buildPromptSortWeight(a))
            return next
          })
        }
      } catch (err) {
        console.warn('[AgentManagerPanel] Failed to hydrate prompt', err)
      }
    },
    [promptMap],
  )

  const computeAgentEntityId = useCallback((agentId: string): number => {
    const agent = agentsRef.current.find((candidate) => candidate.id === agentId)
    if (agent) {
      return agent.tag_entity_id ?? deriveTagEntityId(agent.id)
    }
    return deriveTagEntityId(agentId)
  }, [])

  const refreshTags = useCallback(async (agentList?: AgentRecord[]) => {
    const source = agentList ?? agentsRef.current
    if (!source.length) {
      setAgentTags({})
      return
    }
    const tagLookup = new Map<number, string>()
    for (const agent of source) {
      tagLookup.set(agent.tag_entity_id ?? deriveTagEntityId(agent.id), agent.id)
    }
    try {
      const tagged = await api().services.tags.listEntitiesWithTags('agent')
      if (!mountedRef.current) {
        return
      }
      if (!Array.isArray(tagged)) {
        setAgentTags({})
        return
      }
      const next: Record<string, string[]> = {}
      for (const row of tagged) {
        const agentId = tagLookup.get(row.entityId)
        if (!agentId) {
          continue
        }
        next[agentId] = Array.isArray(row.tags) ? row.tags : []
      }
      setAgentTags(next)
    } catch (err) {
      console.error('[AgentManagerPanel] Failed to load agent tags:', err)
      if (mountedRef.current) {
        setAgentTags({})
      }
    }
  }, [])

  const refreshAgents = useCallback(async (options?: { silent?: boolean; reason?: string }) => {
    const silent = options?.silent ?? false
    const reason = options?.reason ?? 'unspecified'
    console.log(
      `[AgentManagerPanel] refreshAgents start (reason=${reason}, silent=${silent})`,
    )
    if (!silent) {
      setLoading(true)
      setError(null)
    }
    try {
      const list = await api().services.agent.list()
      if (!mountedRef.current) {
        return
      }
      const normalized = Array.isArray(list) ? list.map(normalizeAgent) : []
      setAgents(normalized)
      console.log(
        `[AgentManagerPanel] refreshAgents completed (reason=${reason}) count=${normalized.length}`,
      )
      await refreshTags(normalized)
    } catch (err) {
      console.error('[AgentManagerPanel] Failed to load agents:', err)
      if (mountedRef.current && !silent) {
        setError('Unable to load agents')
        setAgents([])
      }
    } finally {
      if (mountedRef.current) {
        if (!silent) {
          setLoading(false)
        }
        console.log(
          `[AgentManagerPanel] refreshAgents end (reason=${reason}, silent=${silent})`,
        )
      }
    }
  }, [refreshTags])

  const refreshPrompts = useCallback(async () => {
    console.log('[AgentManagerPanel] refreshPrompts start')
    setPromptLoading(true)
    try {
      const list = await api().services.context.listPrompts()
      if (!mountedRef.current) {
        return
      }
      const normalized = Array.isArray(list) ? list : []
      normalized.sort((a, b) => buildPromptSortWeight(b) - buildPromptSortWeight(a))
      setPrompts(normalized)
      console.log(`[AgentManagerPanel] refreshPrompts loaded count=${normalized.length}`)
    } catch (err) {
      console.error('[AgentManagerPanel] Failed to load prompts:', err)
      if (mountedRef.current) {
        setPrompts([])
      }
    } finally {
      if (mountedRef.current) {
        setPromptLoading(false)
        console.log('[AgentManagerPanel] refreshPrompts end')
      }
    }
  }, [])

  useEffect(() => {
    console.log('[AgentManagerPanel] mount -> initial refresh')
    void refreshAgents({ reason: 'initial' })
    void refreshPrompts()
  }, [refreshAgents, refreshPrompts])

  useEffect(() => {
    const handleAgentEvent = async (eventName: string) => {
      console.log(`[AgentManagerPanel] bus event received: ${eventName}`)
      await refreshAgents({ silent: true, reason: `bus:${eventName}` })
    }
    const unsubscribeCreate = bus.on('agent/create', async () => {
      await handleAgentEvent('agent/create')
    })
    const unsubscribeUpdate = bus.on('agent/update', async () => {
      await handleAgentEvent('agent/update')
    })
    const unsubscribeDelete = bus.on('agent/delete', async () => {
      await handleAgentEvent('agent/delete')
    })

    const handleTagEvent = async (_event: string, payload?: { entityType?: string }) => {
      if (payload?.entityType === 'agent') {
        await refreshTags()
      }
    }
    const unsubscribeTagAssign = bus.on('tags/assign', handleTagEvent)
    const unsubscribeTagRemove = bus.on('tags/remove', handleTagEvent)
    const unsubscribeTagReset = bus.on('tags/reset', async () => {
        await refreshTags()
    })

    const handleMetaSet = async (_event: string, payload?: { key?: string; value?: unknown }) => {
      if (payload?.key !== 'active_agent_id') {
        return
      }
      if (!mountedRef.current) {
        return
      }
      if (payload && 'value' in payload) {
        setActiveAgentId(payload.value)
        return
      }
      try {
        const activeId = await api().services.meta.get('active_agent_id')
        if (activeId && mountedRef.current) {
          setActiveAgentId(activeId)
        }
      } catch (err) {
        console.error('[AgentManagerPanel] Failed to refresh active agent ID:', err)
      }
    }
    const unsubscribeMeta = bus.on('meta/set', handleMetaSet)

    return () => {
      unsubscribeCreate()
      unsubscribeUpdate()
      unsubscribeDelete()
      unsubscribeTagAssign()
      unsubscribeTagRemove()
      unsubscribeTagReset()
      unsubscribeMeta()
    }
  }, [refreshAgents, refreshTags])

  useEffect(() => {
    if (!selection) {
      console.log('[AgentManagerPanel] selection cleared')
      setFormState(INITIAL_FORM)
      return
    }
    if (loading) {
      return
    }
    const found = agents.find((agent) => agent.id === selection)
    if (!found) {
      console.log('[AgentManagerPanel] selection missing agent, resetting')
      select({ agent_id: null })
      setFormState(INITIAL_FORM)
      return
    }
    console.log('[AgentManagerPanel] selection updated', selection)
    setFormState((prev) => {
      // Parse tools JSON array
      let parsedTools: string[] = []
      if (found.tools) {
        try {
          const parsed = JSON.parse(found.tools)
          parsedTools = Array.isArray(parsed) ? parsed : []
        } catch (err) {
          console.warn('[AgentManagerPanel] Failed to parse tools JSON:', err)
        }
      }

      const toolsChanged = JSON.stringify(prev.tools) !== JSON.stringify(parsedTools)

      // Only update if values actually changed to prevent unnecessary re-renders
      if (
        prev.name === (found.name ?? '') &&
        prev.system_prompt_hash === (found.system_prompt_hash ?? '') &&
        prev.provider === (found.provider ?? '') &&
        prev.model === (found.model ?? '') &&
        !toolsChanged
      ) {
        return prev
      }
      return {
        name: found.name ?? '',
        system_prompt_hash: found.system_prompt_hash ?? '',
        provider: found.provider ?? '',
        model: found.model ?? '',
        tools: parsedTools,
      }
    })
  }, [agents, loading, select, selection])

  useEffect(() => {
    if (!selectedAgent?.system_prompt_hash) {
      return
    }
    void ensurePromptLoaded(selectedAgent.system_prompt_hash)
  }, [selectedAgent?.system_prompt_hash, ensurePromptLoaded])

  useEffect(() => {
    if (!formState.name.trim()) {
      return
    }
    // Check if form state matches the selected agent to avoid unnecessary saves
    if (selectedAgent) {
      // Parse existing tools for comparison
      let existingTools: string[] = []
      if (selectedAgent.tools) {
        try {
          const parsed = JSON.parse(selectedAgent.tools)
          existingTools = Array.isArray(parsed) ? parsed : []
        } catch {
          existingTools = []
        }
      }

      const isUnchanged =
        formState.name.trim() === (selectedAgent.name ?? '') &&
        formState.system_prompt_hash === (selectedAgent.system_prompt_hash ?? '') &&
        formState.provider === (selectedAgent.provider ?? '') &&
        formState.model === (selectedAgent.model ?? '') &&
        JSON.stringify(formState.tools) === JSON.stringify(existingTools)

      if (isUnchanged) {
        return
      }
    }

    const timer = setTimeout(async () => {
      if (!mountedRef.current) {
        return
      }
      setSaving(true)
      setError(null)
      try {
        if (selectedAgent) {
          // Auto-save existing agent
          await api().services.agent.update(selectedAgent.id, {
            name: formState.name.trim() || null,
            system_prompt_hash: formState.system_prompt_hash ? formState.system_prompt_hash : null,
            provider: formState.provider || null,
            model: formState.model || null,
            tools: formState.tools.length > 0 ? JSON.stringify(formState.tools) : null,
          })
          if (!mountedRef.current) {
            return
          }
          await refreshAgents({ silent: true, reason: 'auto-save' })
          console.log('[AgentManagerPanel] auto-save success', selectedAgent.id)
        } else {
          // Auto-create new agent
          const created = await api().services.agent.create(
            formState.name.trim() || undefined,
            formState.system_prompt_hash ? formState.system_prompt_hash : undefined,
            formState.provider || undefined,
            formState.model || undefined,
            formState.tools.length > 0 ? JSON.stringify(formState.tools) : undefined,
            undefined,
          )
          if (!mountedRef.current) {
            return
          }
          const newAgent = normalizeAgent(created)
          await refreshAgents({ silent: true, reason: 'auto-create' })
          select({ agent_id: newAgent.id })
          console.log('[AgentManagerPanel] auto-create success', newAgent.id)
        }
      } catch (err) {
        console.error('[AgentManagerPanel] Failed to auto-save agent:', err)
        if (mountedRef.current) {
          setError('Failed to save agent')
        }
      } finally {
        if (mountedRef.current) {
          setSaving(false)
        }
      }
    }, 1000)
    return () => clearTimeout(timer)
  }, [selectedAgent, formState.name, formState.system_prompt_hash, formState.provider, formState.model, formState.tools, select, refreshAgents])

  const filteredPrompts = useMemo(() => {
    const term = promptSearch.trim().toLowerCase()
    if (!term) {
      return prompts
    }
    return prompts.filter((prompt) => {
      const snippet = prompt.content.toLowerCase()
      return (
        prompt.hash.toLowerCase().includes(term) ||
        snippet.includes(term) ||
        (prompt.type ?? '').toLowerCase().includes(term)
      )
    })
  }, [promptSearch, prompts])

  const handleSelectAgent = useCallback(
    (agentId: string) => {
      select({ agent_id: agentId })
    },
    [select],
  )

  const handleNewAgent = () => {
    select({ agent_id: null })
    setFormState(INITIAL_FORM)
    setPromptSearch('')
    setActivationNotice(null)
    setError(null)
  }

  const handleFieldChange = <K extends keyof AgentFormState>(field: K, value: AgentFormState[K]) => {
    setFormState((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const handlePromptInputChange = (value: string) => {
    const trimmed = value.trim()
    if (PROMPT_HASH_PATTERN.test(trimmed)) {
      handleFieldChange('system_prompt_hash', trimmed)
      void ensurePromptLoaded(trimmed)
      setPromptSearch('')
    } else {
      setPromptSearch(value)
    }
  }

  const handlePromptSelect = (prompt: StoredPrompt) => {
    handleFieldChange('system_prompt_hash', prompt.hash)
    setPromptSearch('')
    setPromptPreviewExpanded(false)
  }

  const handleClearPrompt = () => {
    handleFieldChange('system_prompt_hash', '')
    setPromptPreviewExpanded(false)
  }

  const handleClearSearch = () => {
    setPromptSearch('')
  }

  const handleTogglePreview = () => {
    setPromptPreviewExpanded((prev) => !prev)
  }

  const currentSlug = useMemo(() => slugifyName(formState.name), [formState.name])

  const providerOptions = useMemo(() => {
    const seen = new Set<string>()
    const ordered: string[] = []
    for (const entry of models) {
      if (!seen.has(entry.provider)) {
        seen.add(entry.provider)
        ordered.push(entry.provider)
      }
    }
    return ordered
  }, [])

  const modelOptions = useMemo(() => {
    if (!formState.provider) {
      return models
    }
    return models.filter((entry) => entry.provider === formState.provider)
  }, [formState.provider])

  const selectedPromptDetails = formState.system_prompt_hash
    ? promptMap[formState.system_prompt_hash] ?? null
    : null

  const tagEntityId = selectedAgent ? selectedAgent.tag_entity_id ?? deriveTagEntityId(selectedAgent.id) : null

  const handleCreate = async () => {
    const trimmedName = formState.name.trim()
    setSaving(true)
    setError(null)
    console.log('[AgentManagerPanel] handleCreate start', {
      name: trimmedName,
      provider: formState.provider,
      model: formState.model,
      system_prompt_hash: formState.system_prompt_hash,
    })
    try {
      const created = await api().services.agent.create(
        trimmedName || undefined,
        formState.system_prompt_hash ? formState.system_prompt_hash : undefined,
        formState.provider || undefined,
        formState.model || undefined,
        formState.tools.length > 0 ? JSON.stringify(formState.tools) : undefined,
        undefined,
      )
      if (!mountedRef.current) {
        return
      }
      const newAgent = normalizeAgent(created)
      await refreshAgents({ silent: true, reason: 'create' })
      select({ agent_id: newAgent.id })
      setActivationNotice(null)
      console.log('[AgentManagerPanel] handleCreate success', newAgent.id)
    } catch (err) {
      console.error('[AgentManagerPanel] Failed to create agent:', err)
      if (mountedRef.current) {
        setError('Failed to create agent')
      }
    } finally {
      if (mountedRef.current) {
        setSaving(false)
      }
    }
  }

  const handleUpdate = async () => {
    if (!selectedAgent) {
      return
    }
    setSaving(true)
    setError(null)
    try {
      await api().services.agent.update(selectedAgent.id, {
        name: formState.name.trim() || null,
        system_prompt_hash: formState.system_prompt_hash ? formState.system_prompt_hash : null,
        provider: formState.provider || null,
        model: formState.model || null,
        tools: formState.tools.length > 0 ? JSON.stringify(formState.tools) : null,
      })
      if (!mountedRef.current) {
        return
      }
      await refreshAgents({ silent: true, reason: 'update' })
      setActivationNotice(null)
      console.log('[AgentManagerPanel] handleUpdate success', selectedAgent.id)
    } catch (err) {
      console.error('[AgentManagerPanel] Failed to update agent:', err)
      if (mountedRef.current) {
        setError('Failed to update agent')
      }
    } finally {
      if (mountedRef.current) {
        setSaving(false)
      }
    }
  }

  const handleDelete = async () => {
    if (!selectedAgent) {
      return
    }
    setDeleting(true)
    setError(null)
    try {
      await api().services.agent.delete(selectedAgent.id)
      if (!mountedRef.current) {
        return
      }
      await refreshAgents({ silent: true, reason: 'delete' })
      select({ agent_id: null })
      setFormState(INITIAL_FORM)
      setActivationNotice(null)
      console.log('[AgentManagerPanel] handleDelete success', selectedAgent.id)
    } catch (err) {
      console.error('[AgentManagerPanel] Failed to delete agent:', err)
      if (mountedRef.current) {
        setError('Failed to delete agent')
      }
    } finally {
      if (mountedRef.current) {
        setDeleting(false)
      }
    }
  }

  const handleClone = async () => {
    if (!selectedAgent) {
      return
    }
    setCloning(true)
    setError(null)
    try {
      const clonedAgent = await api().services.agent.create(
        selectedAgent.name ? `${selectedAgent.name} (copy)` : undefined,
        selectedAgent.system_prompt_hash ? selectedAgent.system_prompt_hash : undefined,
        selectedAgent.provider ? selectedAgent.provider : undefined,
        selectedAgent.model ? selectedAgent.model : undefined,
        selectedAgent.tools ? selectedAgent.tools : undefined,
        selectedAgent.last_message_id ? selectedAgent.last_message_id : undefined,
        undefined,
      )
      await api().services.files.cloneFilesystem(selectedAgent.id, clonedAgent.id)

      if (!mountedRef.current) {
        return
      }
      const newAgent = normalizeAgent(clonedAgent)
      await refreshAgents({ silent: true, reason: 'clone' })
      select({ agent_id: newAgent.id })
      console.log('[AgentManagerPanel] handleClone success', newAgent.id)
    } catch (err) {
      console.error('[AgentManagerPanel] Failed to clone agent:', err)
      if (mountedRef.current) {
        setError('Failed to clone agent')
      }
    } finally {
      if (mountedRef.current) {
        setCloning(false)
      }
    }
  }

  const handleActivate = async () => {
    if (!selectedAgent) {
      return
    }
    setSaving(true)
    setError(null)
    try {
      await api().services.meta.set('active_agent_id', selectedAgent.id)
      if (!mountedRef.current) {
        return
      }
      setActiveAgentId(selectedAgent.id)
      setActivationNotice('Agent activated')
      console.log('[AgentManagerPanel] handleActivate success', selectedAgent.id)
    } catch (err) {
      console.error('[AgentManagerPanel] Failed to activate agent:', err)
      if (mountedRef.current) {
        setError('Failed to activate agent')
      }
    } finally {
      if (mountedRef.current) {
        setSaving(false)
      }
    }
  }

  const openTagEditor = (agentId: string, anchorEl: HTMLElement) => {
    setTagEditorState({ agentId, entityId: computeAgentEntityId(agentId), anchorEl })
  }

  const closeTagEditor = () => {
    setTagEditorState(null)
  }

  if (loading) {
    return (
      <div className="panel-shell">
        <div className="panel-content panel-content--horizontal">
          <div className="empty-state">
            <p>Loading agents...</p>
          </div>
        </div>
      </div>
    )
  }

  if (!agents.length && !selection) {
    return (
      <div className="panel-shell">
        <div className="panel-controls">
          <button type="button" className="btn" onClick={handleNewAgent}>
            New agent
          </button>
        </div>
        <div className="panel-content panel-content--horizontal">
          <div className="empty-state">
            <p>No agents yet</p>
            <p className="empty-state-subtitle">Create an agent to start configuring prompts and models</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="panel-shell">
      <div className="panel-controls">
        <button type="button" className="btn" onClick={handleNewAgent}>
          New agent
        </button>
        {error && <span className="panel-error">{error}</span>}
      </div>
      <div className="panel-content panel-content--horizontal agent-manager">
        <div className="agent-manager__column agent-manager__column--editor">
          <div className="agent-editor__wrapper">
            <form
              className="agent-editor"
              onSubmit={(event) => {
                event.preventDefault()
              }}
            >
              <div className="agent-editor__row">
                <div className="agent-editor__section">
                  <label className="agent-editor__label" htmlFor="agent-name">
                    Name
                  </label>
                  <input
                    id="agent-name"
                    type="text"
                    className="agent-editor__input"
                    value={formState.name}
                    onChange={(event) => handleFieldChange('name', event.target.value)}
                    placeholder="My agent"
                    autoComplete="off"
                  />
                </div>
                <div className="agent-editor__section">
                  <label className="agent-editor__label">Agent ID</label>
                  {selectedAgent ? (
                    <ClickToCopy
                      value={selectedAgent.id}
                      className="agent-editor__prompt-hash"
                      title="Click to copy agent ID"
                      ariaLabel="Copy agent ID to clipboard"
                    />
                  ) : (
                    <div className="agent-editor__slug">—</div>
                  )}
                </div>
              </div>

              <div className="agent-editor__row">
                <div className="agent-editor__section">
                  <label className="agent-editor__label" htmlFor="agent-provider">
                    Provider
                  </label>
                  <select
                    id="agent-provider"
                    className="agent-editor__input"
                    value={formState.provider}
                    onChange={(event) => handleFieldChange('provider', event.target.value)}
                  >
                    <option value="">Select provider</option>
                    {providerOptions.map((provider) => (
                      <option key={provider} value={provider}>
                        {provider}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="agent-editor__section">
                  <label className="agent-editor__label" htmlFor="agent-model">
                    Model
                  </label>
                  <select
                    id="agent-model"
                    className="agent-editor__input"
                    value={formState.model}
                    onChange={(event) => handleFieldChange('model', event.target.value)}
                  >
                    <option value="">Select model</option>
                    {modelOptions.map((entry) => (
                      <option key={entry.key} value={entry.model}>
                        {entry.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="agent-editor__section">
                <ToolSelector
                  selectedTools={formState.tools}
                  onChange={(tools) => handleFieldChange('tools', tools)}
                />
              </div>

              <div className="agent-editor__section agent-editor__section--flex">
                <label className="agent-editor__label" htmlFor="agent-prompt-search">
                  Prompt
                </label>
                {selectedPromptDetails ? (
                  <div className="agent-editor__prompt-summary">
                    <div className="agent-editor__prompt-summary-header">
                      <ClickToCopy
                        value={selectedPromptDetails.hash}
                        className="agent-editor__prompt-hash"
                        title="Click to copy prompt hash"
                        ariaLabel="Copy prompt hash to clipboard"
                      />
                      <button
                        type="button"
                        className="tool-selector__action"
                        onClick={handleClearPrompt}
                        aria-label="Remove selected prompt"
                      >
                        Clear
                      </button>
                    </div>
                    <div className="agent-editor__prompt-tags">
                      {selectedPromptDetails.is_composed && <span className="badge badge--neutral">composite</span>}
                      {selectedPromptDetails.type && (
                        <span className="badge badge--positive">{selectedPromptDetails.type}</span>
                      )}
                    </div>
                    <p
                      className={`agent-editor__prompt-preview${promptPreviewExpanded ? ' agent-editor__prompt-preview--expanded' : ''}`}
                      onClick={handleTogglePreview}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault()
                          handleTogglePreview()
                        }
                      }}
                      title={promptPreviewExpanded ? 'Click to collapse' : 'Click to expand full prompt'}
                    >
                      {promptPreviewExpanded ? selectedPromptDetails.content : formatPromptSnippet(selectedPromptDetails.content)}
                    </p>
                  </div>
                ) : formState.system_prompt_hash ? (
                  <div className="agent-editor__prompt-summary">
                    <div className="agent-editor__prompt-summary-header">
                      <ClickToCopy
                        value={formState.system_prompt_hash}
                        className="agent-editor__prompt-hash"
                        title="Click to copy prompt hash"
                        ariaLabel="Copy prompt hash to clipboard"
                      />
                      <button
                        type="button"
                        className="tool-selector__action"
                        onClick={handleClearPrompt}
                        aria-label="Remove selected prompt"
                      >
                        Clear
                      </button>
                    </div>
                    <p className="agent-editor__prompt-preview">Prompt details not loaded</p>
                  </div>
                ) : (
                  <p className="agent-editor__prompt-placeholder">No prompt selected</p>
                )}
                <div className="agent-editor__prompt-search">
                  <input
                    id="agent-prompt-search"
                    type="text"
                    className="agent-editor__input"
                    value={promptSearch}
                    onChange={(event) => handlePromptInputChange(event.target.value)}
                    placeholder="Search prompts or paste a hash"
                    autoComplete="off"
                  />
                  {promptSearch && (
                    <button
                      type="button"
                      className="tool-selector__action agent-editor__search-clear"
                      onClick={handleClearSearch}
                      aria-label="Clear search"
                    >
                      Clear
                    </button>
                  )}
                </div>
                {promptSearch.trim() && (
                  <div className="agent-editor__prompt-results">
                    {promptLoading && <div className="agent-editor__prompt-loading">Loading prompts...</div>}
                    {!promptLoading &&
                      filteredPrompts.slice(0, 12).map((prompt) => {
                        const isSelected = prompt.hash === formState.system_prompt_hash
                        return (
                          <button
                            key={prompt.hash}
                            type="button"
                            className={`agent-editor__prompt-option${
                              isSelected ? ' agent-editor__prompt-option--selected' : ''
                            }`}
                            onClick={() => handlePromptSelect(prompt)}
                          >
                            <div className="agent-editor__prompt-option-header">
                              <span className="agent-editor__prompt-option-hash">{prompt.hash}</span>
                              <div className="agent-editor__prompt-option-badges">
                                {prompt.is_composed && <span className="badge badge--neutral">composite</span>}
                                {prompt.type && <span className="badge badge--positive">{prompt.type}</span>}
                              </div>
                            </div>
                            <p className="agent-editor__prompt-option-preview">
                              {formatPromptSnippet(prompt.content)}
                            </p>
                          </button>
                        )
                      })}
                    {!promptLoading && filteredPrompts.length === 0 && (
                      <div className="agent-editor__prompt-empty">No prompts match your search</div>
                    )}
                  </div>
                )}
              </div>
            </form>
          </div>
          <div className="agent-editor__footer">
            {activationNotice && <p className="agent-editor__notice">{activationNotice}</p>}
            <div className="agent-editor__button-group">
              {selectedAgent && (
                <button
                  type="button"
                  className="btn btn--delete"
                  onClick={() => {
                    void handleDelete()
                  }}
                  disabled={deleting}
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              )}
              {selectedAgent && (
                <button
                  type="button"
                  className="btn"
                  onClick={() => {
                    void handleClone()
                  }}
                  disabled={cloning}
                >
                  {cloning ? 'Cloning...' : 'Clone'}
                </button>
              )}
              {selectedAgent && (
                <button
                  type="button"
                  className="btn"
                  onClick={() => {
                    void handleActivate()
                  }}
                  disabled={saving}
                >
                  Activate
                </button>
              )}
              {selectedAgent && (
                <button
                  type="button"
                  className="btn"
                  onClick={(event) => {
                    event.preventDefault()
                    if (tagEntityId) {
                      openTagEditor(selectedAgent.id, event.currentTarget)
                    }
                  }}
                >
                  Tags
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="agent-manager__column agent-manager__column--list">
          <ul className="agent-list">
            {agents.map((agent) => {
              const isSelected = agent.id === selection
              const tags = agentTags[agent.id] ?? []
              const slug = agent.name ? slugifyName(agent.name) : ''
              return (
                <li key={agent.id}>
                  <button
                    type="button"
                    className={`agent-list__row${isSelected ? ' agent-list__row--selected' : ''}`}
                    onClick={() => handleSelectAgent(agent.id)}
                  >
                    <div className="agent-list__row-header">
                      <div className="agent-list__name-section">
                        <div className="agent-list__name-row">
                          {activeAgentId === agent.id && <div className="agent-list__led"></div>}
                          <span className="agent-list__name">{agent.name ?? 'Untitled agent'}</span>
                        </div>
                        {slug && <span className="agent-list__slug">{slug}</span>}
                      </div>
                      <div className="agent-list__tags">
                        {tags.length > 0 ? (
                          tags.map((tag) => (
                            <button
                              key={tag}
                              type="button"
                              className="tag-badge tag-badge--assigned tag-badge--interactive"
                              onClick={(event) => {
                                event.stopPropagation()
                                openTagEditor(agent.id, event.currentTarget)
                              }}
                            >
                              {tag}
                            </button>
                          ))
                        ) : (
                          <button
                            type="button"
                            className="tag-badge tag-badge--empty"
                            onClick={(event) => {
                              event.stopPropagation()
                              openTagEditor(agent.id, event.currentTarget)
                            }}
                          >
                            Tags
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="agent-list__row-footer">
                      {agent.provider && <span className="agent-list__provider">{agent.provider}</span>}
                      {agent.model && <span className="agent-list__model">{modelKeyMap[agent.model] ?? agent.model}</span>}
                    </div>
                  </button>
                </li>
              )
            })}
          </ul>
        </div>
      </div>
      {tagEditorState && (
        <TagEditor
          entityType="agent"
          entityId={tagEditorState.entityId}
          onClose={closeTagEditor}
          anchorEl={tagEditorState.anchorEl}
        />
      )}
    </div>
  )
}
